import { toast } from 'sonner'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { useUpdateSubtotalDiscount } from '@/features/purchase-orders/hooks'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import type { components } from '@/api/schema'

type PurchaseOrder = components['schemas']['PurchaseOrderDetailRead']
/** Subtotal, the supplier's extra subtotal discount, and the PO total. */
export function POTotals({ po, editMode }: { po: PurchaseOrder; editMode: boolean }) {
  const discount = po.subtotal_discount ?? null
  const discountAmount = Number(discount?.amount ?? 0)
  const hasDiscount = discount != null && discountAmount > 0
  const subtotal = Number(po.total) + discountAmount

  return (
    <dl className="ml-auto grid w-full max-w-sm grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5 pr-2 text-sm tabular-nums">
      {(hasDiscount || editMode) && (
        <>
          <dt className="text-xs text-muted-foreground">Subtotal</dt>
          <dd className="text-right">{formatMoney(subtotal)}</dd>
        </>
      )}
      {editMode ? (
        <SubtotalDiscountEditor
          key={`${discount?.discount_type}:${discount?.value}`}
          poUuid={po.uuid}
          discount={discount}
        />
      ) : (
        hasDiscount && (
          <>
            <dt className="text-xs text-muted-foreground">
              {discount.discount_type === 'percent'
                ? `Discount (${formatPercent(discount.value)})`
                : 'Discount'}
            </dt>
            <dd className="text-right">-{formatMoney(discountAmount)}</dd>
          </>
        )
      )}
      <dt className="text-xs text-muted-foreground">Total</dt>
      <dd className="text-right text-base font-semibold">{formatMoney(po.total)}</dd>
    </dl>
  )
}

/** "5%" is a percent discount, "120" or "$120" an amount; blank removes the discount. */
function parseSubtotalDiscount(
  raw: string,
): components['schemas']['PurchaseOrderSubtotalDiscountUpdate'] | 'invalid' {
  const text = raw.replace(/[$,\s]/g, '')
  if (!text) return { discount_type: null, value: null }
  const percent = text.endsWith('%')
  const number = percent ? text.slice(0, -1) : text
  const parsed = Number(number)
  if (!number || !Number.isFinite(parsed) || parsed <= 0 || (percent && parsed >= 100)) return 'invalid'
  return { discount_type: percent ? 'percent' : 'amount', value: number }
}

/** The supplier's extra subtotal discount as a click-to-edit value: "5%" or "120". */
function SubtotalDiscountEditor({
  poUuid,
  discount,
}: {
  poUuid: string
  discount: components['schemas']['PurchaseOrderSubtotalDiscountRead'] | null
}) {
  const mutation = useUpdateSubtotalDiscount(poUuid)
  const current =
    discount == null
      ? ''
      : discount.discount_type === 'percent'
        ? `${Number(discount.value)}%`
        : String(Number(discount.value))

  const save = (raw: string) => {
    const body = parseSubtotalDiscount(raw)
    if (body === 'invalid') {
      toast.error('Enter an amount like 120 or a percent like 5%')
      return
    }
    mutation.mutate(body, {
      onSuccess: () =>
        toast.success(body.discount_type ? 'Subtotal discount saved' : 'Subtotal discount removed'),
      onError: (err) =>
        toast.error(
          err && typeof err === 'object' && 'detail' in err && typeof err.detail === 'string'
            ? err.detail
            : 'Failed to save the subtotal discount',
        ),
    })
  }

  return (
    <>
      <dt className="text-xs text-muted-foreground">
        {discount?.discount_type === 'percent'
          ? `Discount (${formatPercent(discount.value)})`
          : 'Discount'}
      </dt>
      <dd className="flex justify-end">
        <POInlineEditableField
          value={current}
          displayValue={discount ? `-${formatMoney(discount.amount)}` : undefined}
          placeholder="Add discount"
          align="right"
          ariaLabel="Subtotal discount"
          title="Amount (120) or percent (5%); clear to remove"
          pending={mutation.isPending}
          inputClassName="w-28"
          buttonClassName={cn('max-w-none shrink-0', !discount && 'text-muted-foreground')}
          onConfirm={save}
        />
      </dd>
    </>
  )
}
